import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { cn } from '../../../lib/utils';

interface FloatingTestBotProps {
  readonly embedIsReady: boolean;
  readonly visible: boolean;
}

const DEFAULT_OFFSET = 16;
const JAPETO_ROOT_ID = 'japeto-root';
const CHATBOT_CONTAINER_SELECTOR = '[data-testid="chatbot-component"]';

const OVERRIDE_STYLE_ID = 'japeto-floating-overrides';
const DRAG_STYLE_ID = 'japeto-drag-no-select';

const OVERRIDE_CSS = `
  ${CHATBOT_CONTAINER_SELECTOR} {
    position: relative !important;
    bottom: unset !important;
    right: unset !important;
    z-index: auto !important;
  }
  #response-container {
    flex: 1 1 0 !important;
    height: auto !important;
    min-height: 0 !important;
  }
`;

const HEADER_DRAG_ZONE = 60;

function injectOverrideStyles(shadow: ShadowRoot): void {
  if (shadow.querySelector(`#${OVERRIDE_STYLE_ID}`)) return;
  const style = document.createElement('style');
  style.id = OVERRIDE_STYLE_ID;
  style.textContent = OVERRIDE_CSS;
  shadow.appendChild(style);
}

function removeOverrideStyles(shadow: ShadowRoot): void {
  shadow.querySelector(`#${OVERRIDE_STYLE_ID}`)?.remove();
}

function disableShadowSelection(shadow: ShadowRoot): void {
  if (shadow.querySelector(`#${DRAG_STYLE_ID}`)) return;
  const style = document.createElement('style');
  style.id = DRAG_STYLE_ID;
  style.textContent =
    '* { user-select: none !important; -webkit-user-select: none !important; }';
  shadow.appendChild(style);
}

function enableShadowSelection(shadow: ShadowRoot): void {
  shadow.querySelector(`#${DRAG_STYLE_ID}`)?.remove();
}

function getShadowRoot(): ShadowRoot | null {
  return document.getElementById(JAPETO_ROOT_ID)?.shadowRoot ?? null;
}

type Position =
  | { mode: 'default' }
  | { mode: 'explicit'; bottom: number; right: number };

const DRAG_THRESHOLD = 4;
const Z_INDEX = 'z-[9998]';

function clampDragPosition(
  clientX: number,
  clientY: number,
  offset: { x: number; y: number },
  size: { w: number; h: number }
): { bottom: number; right: number } {
  const { innerWidth, innerHeight } = globalThis;
  const rawTop = clientY - offset.y;
  const rawLeft = clientX - offset.x;

  const clampedTop = Math.max(0, Math.min(innerHeight - size.h, rawTop));
  const clampedLeft = Math.max(0, Math.min(innerWidth - size.w, rawLeft));

  return {
    bottom: innerHeight - clampedTop - size.h,
    right: innerWidth - clampedLeft - size.w
  };
}

function FloatingTestBot({
  embedIsReady,
  visible
}: FloatingTestBotProps): React.ReactElement | null {
  const [position, setPosition] = useState<Position>({ mode: 'default' });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const dragOverlayRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const didDrag = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragSize = useRef({ w: 0, h: 0 });
  const dragStart = useRef({ x: 0, y: 0 });
  const wasVisible = useRef(false);

  const pendingDrag = useRef(false);
  const wrapperDragging = useRef(false);

  useLayoutEffect(() => {
    if (!embedIsReady || !contentRef.current) return undefined;

    const japetoRoot = document.getElementById(JAPETO_ROOT_ID);
    if (!japetoRoot) return undefined;

    if (japetoRoot.parentElement !== contentRef.current) {
      contentRef.current.appendChild(japetoRoot);
    }

    const shadow = japetoRoot.shadowRoot;
    if (shadow) {
      injectOverrideStyles(shadow);
    }

    (globalThis as unknown as Window).japetoApi?.showChatbot();

    return () => {
      (globalThis as unknown as Window).japetoApi?.hideChatbot();
      if (shadow) {
        removeOverrideStyles(shadow);
      }
      if (japetoRoot.isConnected) {
        document.body.appendChild(japetoRoot);
      }
    };
  }, [embedIsReady]);

  const clampToViewport = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || wrapperDragging.current) return;

    const rect = wrapper.getBoundingClientRect();
    const { innerWidth, innerHeight } = globalThis;
    const pad = DEFAULT_OFFSET;

    const currentBottom = innerHeight - rect.bottom;
    const currentRight = innerWidth - rect.right;

    const clampedBottom = Math.max(
      pad,
      Math.min(innerHeight - pad - rect.height, currentBottom)
    );
    const clampedRight = Math.max(
      pad,
      Math.min(innerWidth - pad - rect.width, currentRight)
    );

    if (
      Math.abs(clampedBottom - currentBottom) > 1 ||
      Math.abs(clampedRight - currentRight) > 1
    ) {
      setPosition({
        mode: 'explicit',
        bottom: clampedBottom,
        right: clampedRight
      });
    }
  }, []);

  useLayoutEffect(() => {
    const justShown = visible && !wasVisible.current;
    wasVisible.current = visible;
    if (justShown) clampToViewport();
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!visible || !wrapper) return undefined;

    const observer = new ResizeObserver(clampToViewport);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [visible, clampToViewport]);

  const showDragOverlay = useCallback(() => {
    if (dragOverlayRef.current) {
      dragOverlayRef.current.style.display = 'block';
    }
  }, []);

  const hideDragOverlay = useCallback(() => {
    setTimeout(() => {
      if (dragOverlayRef.current) {
        dragOverlayRef.current.style.display = 'none';
      }
    }, 0);
  }, []);

  const handleWrapperPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();
      const yInWrapper = e.clientY - rect.top;
      if (yInWrapper > HEADER_DRAG_ZONE) return;

      pendingDrag.current = true;
      didDrag.current = false;
      dragStart.current = { x: e.clientX, y: e.clientY };
      dragSize.current = { w: rect.width, h: rect.height };
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };

      if (position.mode === 'default') {
        setPosition({
          mode: 'explicit',
          bottom: globalThis.innerHeight - rect.bottom,
          right: globalThis.innerWidth - rect.right
        });
      }
    },
    [position.mode]
  );

  useEffect(() => {
    const handleDocMove = (e: PointerEvent): void => {
      if (!pendingDrag.current && !wrapperDragging.current) return;

      if (pendingDrag.current && !wrapperDragging.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

        pendingDrag.current = false;
        wrapperDragging.current = true;
        dragging.current = true;
        didDrag.current = true;
        document.body.style.userSelect = 'none';
        const shadow = getShadowRoot();
        if (shadow) disableShadowSelection(shadow);
        showDragOverlay();
      }

      if (wrapperDragging.current) {
        e.preventDefault();

        const clamped = clampDragPosition(
          e.clientX,
          e.clientY,
          dragOffset.current,
          dragSize.current
        );
        setPosition({ mode: 'explicit', ...clamped });
      }
    };

    const handleDocUp = (): void => {
      const wasDragging = wrapperDragging.current;
      pendingDrag.current = false;
      wrapperDragging.current = false;
      dragging.current = false;

      if (wasDragging) {
        hideDragOverlay();
        document.body.style.userSelect = '';
        const shadow = getShadowRoot();
        if (shadow) enableShadowSelection(shadow);
      }
    };

    document.addEventListener('pointermove', handleDocMove);
    document.addEventListener('pointerup', handleDocUp);
    return () => {
      document.removeEventListener('pointermove', handleDocMove);
      document.removeEventListener('pointerup', handleDocUp);
      document.body.style.userSelect = '';
      const shadow = getShadowRoot();
      if (shadow) enableShadowSelection(shadow);
    };
  }, [showDragOverlay, hideDragOverlay]);

  if (!embedIsReady) return null;

  const positionStyle: React.CSSProperties =
    position.mode === 'default'
      ? { bottom: DEFAULT_OFFSET, right: DEFAULT_OFFSET }
      : { bottom: position.bottom, right: position.right };

  return (
    <div
      ref={wrapperRef}
      className={cn(`fixed ${Z_INDEX} w-fit`, !visible && 'hidden')}
      style={positionStyle}
      onPointerDown={handleWrapperPointerDown}
    >
      <div
        ref={dragOverlayRef}
        className='absolute inset-0 z-[1]'
        style={{ display: 'none' }}
      />

      <div ref={contentRef} />
    </div>
  );
}

export default FloatingTestBot;
